import React from 'react';
import { List } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash-es';
import { httpClient } from '@s20/services/httpClient';
import { ApiRoutes } from '../../Common';
import { toasty } from '../../common/forms/FormElements';
import { SaveButton } from '../../common/forms/SaveButton';
import ValidatedSelect from '../../common/forms/ValidatedSelect';

/**
 * Shared class component for rejecting billing and payroll from a timesheet.
 * Can be used by both Admin Tools and Support Tools pages.
 *
 * Props:
 * @param {function} onSuccess - Callback after successful submission (e.g., refreshUsageHistory)
 */
class RejectBillingPayrollForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            saving: false,
            timesheetOptions: [],
            selectedTimesheetOption: null,
        };
    }

    loadTimesheetOptions = debounce((inputValue, callback) => {
        httpClient.js(ApiRoutes.typeAheads.timesheetNumberSearch(inputValue))
            .then((data) => {
                this.setState({ timesheetOptions: data });
                callback(data);
            });
    }, 250);

    onTimesheetChanged = (selectedOption) => {
        this.setState({ selectedTimesheetOption: selectedOption });
    };

    onSubmit = async (e) => {
        if (e) e.preventDefault();

        const { selectedTimesheetOption } = this.state;

        if (!selectedTimesheetOption) {
            toasty.error('Please select a timesheet');
            return;
        }

        this.setState({ saving: true });

        try {
            await httpClient.post(ApiRoutes.adminTool.rejectBillingPayroll(), {
                timesheetId: selectedTimesheetOption.value,
            });

            this.setState({
                selectedTimesheetOption: null,
                timesheetOptions: [],
            });

            toasty.success('Billing Payroll Rejection Success');

            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } catch (err) {
            console.error(err);
            toasty.error('Error: Billing Payroll Rejection Failed. Please Check Timesheet Number');
            if (this.props.onSuccess) {
                await this.props.onSuccess();
            }
        } finally {
            this.setState({ saving: false });
        }
    };

    render() {
        const { saving, selectedTimesheetOption } = this.state;
        const isSubmitDisabled = saving || !selectedTimesheetOption;

        return (
            <form onSubmit={this.onSubmit}>
                <ValidatedSelect
                    component={AsyncSelect}
                    loadOptions={this.loadTimesheetOptions}
                    placeholder="Type Timesheet Number Here"
                    id="timesheetNumber"
                    label="Timesheet Number"
                    value={selectedTimesheetOption}
                    onChange={this.onTimesheetChanged}
                    cacheOptions
                    required
                    error="Timesheet Number is Required."
                />

                <div className="d-flex justify-content-center mt-4">
                    <SaveButton
                        saving={saving}
                        disabled={isSubmitDisabled}
                        label="Submit"
                    />
                </div>
            </form>
        );
    }
}

/**
 * Renders the execution history details for RejectBillingPayroll.
 * Can be used by both Admin and SupportTools ExecutionHistory components.
 */
export const RejectBillingPayrollHistoryDetails = ({ record }) => {
    const input = record?.input ?? {};
    const output = record?.output ?? {};

    return (
        <>
            <div>
                <List type="unstyled">
                    {input.timesheetNumber && (
                        <li>
                            <strong>Timesheet:</strong>
                            {' '}
                            {input.timesheetNumber}
                        </li>
                    )}
                </List>
            </div>

            <div>
                <List type="unstyled">
                    {output.removedBillingDetails && (
                        <li>
                            <strong>Removed Billing:</strong>
                            {' '}
                            {output.removedBillingDetails}
                        </li>
                    )}
                    {output.removedPayrollCalculationIds && (
                        <li>
                            <strong>Removed Payroll Calculations (ID):</strong>
                            {' '}
                            {output.removedPayrollCalculationIds}
                        </li>
                    )}
                    {output.removedPayrollIds && (
                        <li>
                            <strong>Removed Payrolls (ID):</strong>
                            {' '}
                            {output.removedPayrollIds}
                        </li>
                    )}
                    {!output.removedPayrollIds && !output.removedPayrollCalculationIds && !output.removedBillingDetails && (
                        <li>
                            <strong>Result:</strong>
                            {' '}
                            Billing Payroll Removal Success
                        </li>
                    )}
                </List>
            </div>
        </>
    );
};

export { RejectBillingPayrollForm };
export default RejectBillingPayrollForm;
